import React from 'react';
import { Editor } from '@tiptap/react';
import {
    Bold, Italic, Underline, Heading1, Heading2,
    List, ListOrdered, Quote, Minus, ChevronDown
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface FixedToolbarProps {
    editor: Editor | null;
    className?: string;
    style?: React.CSSProperties;
    themeColors: any;
}

const TOOLBAR_ITEMS = [
    { id: 'bold', icon: Bold, label: 'Bold', shortcut: '⌘B', action: (e: Editor) => e.chain().focus().toggleBold().run(), isActive: (e: Editor) => e.isActive('bold') },
    { id: 'italic', icon: Italic, label: 'Italic', shortcut: '⌘I', action: (e: Editor) => e.chain().focus().toggleItalic().run(), isActive: (e: Editor) => e.isActive('italic') },
    { id: 'underline', icon: Underline, label: 'Underline', shortcut: '⌘U', action: (e: Editor) => e.chain().focus().toggleUnderline().run(), isActive: (e: Editor) => e.isActive('underline') },
    { divider: true },
    { id: 'h1', icon: Heading1, label: 'Heading 1', shortcut: '⌥⌘1', action: (e: Editor) => e.chain().focus().toggleHeading({ level: 1 }).run(), isActive: (e: Editor) => e.isActive('heading', { level: 1 }) },
    { id: 'h2', icon: Heading2, label: 'Heading 2', shortcut: '⌥⌘2', action: (e: Editor) => e.chain().focus().toggleHeading({ level: 2 }).run(), isActive: (e: Editor) => e.isActive('heading', { level: 2 }) },
    { divider: true },
    { id: 'bulletList', icon: List, label: 'Bullet List', shortcut: '⇧⌘8', action: (e: Editor) => e.chain().focus().toggleBulletList().run(), isActive: (e: Editor) => e.isActive('bulletList') },
    { id: 'orderedList', icon: ListOrdered, label: 'Ordered List', shortcut: '⇧⌘7', action: (e: Editor) => e.chain().focus().toggleOrderedList().run(), isActive: (e: Editor) => e.isActive('orderedList') },
    { id: 'blockquote', icon: Quote, label: 'Quote', shortcut: '⇧⌘B', action: (e: Editor) => e.chain().focus().toggleBlockquote().run(), isActive: (e: Editor) => e.isActive('blockquote') },
    { id: 'horizontalRule', icon: Minus, label: 'Divider', shortcut: '---', action: (e: Editor) => e.chain().focus().setHorizontalRule().run(), isActive: () => false },
];

export function FixedToolbar({ editor, className, style, themeColors }: FixedToolbarProps) {
    const [openDropdown, setOpenDropdown] = React.useState<string | null>(null);

    if (!editor) return null;

    return (
        <div
            className={cn(
                "flex items-center gap-1 p-1 backdrop-blur-md border rounded-lg shadow-sm mb-4 sticky top-0 z-40 transition-colors duration-300",
                className
            )}
            style={{
                borderColor: themeColors.border,
                backgroundColor: themeColors.bgSecondary + 'CC', // 80% opacity
                ...style
            }}
        >
            {TOOLBAR_ITEMS.map((item, idx) => {
                if ('divider' in item) {
                    return <div key={`div-${idx}`} className="w-px h-4 bg-zinc-200 dark:bg-zinc-800 mx-1" />;
                }

                const ActiveIcon = item.icon;
                const active = item.isActive(editor);

                return (
                    <div key={item.id} className="relative flex items-center">
                        <button
                            onClick={() => item.action(editor)}
                            className={cn(
                                "p-2 rounded transition-all duration-200 group flex items-center gap-1",
                                active
                                    ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                                    : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-500 dark:text-zinc-400"
                            )}
                            title={`${item.label} (${item.shortcut})`}
                        >
                            <ActiveIcon size={16} />
                        </button>

                        <div className="group relative">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdown(openDropdown === item.id ? null : item.id);
                                }}
                                className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-300 dark:text-zinc-600 transition-colors"
                            >
                                <ChevronDown size={10} />
                            </button>

                            {openDropdown === item.id && (
                                <>
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={() => setOpenDropdown(null)}
                                    />
                                    <div
                                        className="absolute top-full left-0 mt-1 w-32 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded shadow-xl z-50 p-2 text-[10px]"
                                        style={{ borderColor: themeColors.border }}
                                    >
                                        <div className="flex justify-between items-center opacity-70">
                                            <span>Shortcut</span>
                                            <code className="bg-zinc-100 dark:bg-zinc-800 px-1 rounded">{item.shortcut}</code>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
